import {
  Text,
  View,
  StyleSheet,
  Svg,
  Path,
  Line,
  Font,
} from '@react-pdf/renderer';

import MTEverydaySansBold from '../../../fonts/MTEverydaySans-Bold.woff';

const GROTESK_FONT_SOURCE_URLS = {
  regular400:
    'https://fonts.gstatic.com/s/schibstedgrotesk/v3/JqzK5SSPQuCQF3t8uOwiUL-taUTtarVKQ9vZ6pJJWlMNIsEAT8JuXFGVOQ.ttf',
  medium500:
    'https://fonts.gstatic.com/s/schibstedgrotesk/v3/JqzK5SSPQuCQF3t8uOwiUL-taUTtarVKQ9vZ6pJJWlMNEMEAT8JuXFGVOQ.ttf',
  semibold600:
    'https://fonts.gstatic.com/s/schibstedgrotesk/v3/JqzK5SSPQuCQF3t8uOwiUL-taUTtarVKQ9vZ6pJJWlMN_MYAT8JuXFGVOQ.ttf',
  italic400:
    'https://fonts.gstatic.com/s/schibstedgrotesk/v3/JqzI5SSPQuCQF3t8uOwiUL-taUTtap9DcSQBg_nT9FQY6oLoDMhqflSFOTXs.ttf',
  mediumItalic500:
    'https://fonts.gstatic.com/s/schibstedgrotesk/v3/JqzI5SSPQuCQF3t8uOwiUL-taUTtap9DcSQBg_nT9FQY6oLaDMhqflSFOTXs.ttf',
  semiboldItalic600:
    'https://fonts.gstatic.com/s/schibstedgrotesk/v3/JqzI5SSPQuCQF3t8uOwiUL-taUTtap9DcSQBg_nT9FQY6oI2C8hqflSFOTXs.ttf',
};

const FONT_NAME = 'Schibsted Grotesk';
const MT_FONT_NAME = 'MTEverydaySans';

Font.register({
  family: FONT_NAME,
  fonts: [
    {
      src: GROTESK_FONT_SOURCE_URLS.regular400,
    },
    {
      src: GROTESK_FONT_SOURCE_URLS.italic400,
      fontStyle: 'italic',
    },
    {
      src: GROTESK_FONT_SOURCE_URLS.medium500,
      fontWeight: 'medium',
    },
    {
      src: GROTESK_FONT_SOURCE_URLS.mediumItalic500,
      fontStyle: 'italic',
      fontWeight: 'medium',
    },
    {
      src: GROTESK_FONT_SOURCE_URLS.semibold600,
      fontWeight: 'semibold',
    },
    {
      src: GROTESK_FONT_SOURCE_URLS.semiboldItalic600,
      fontStyle: 'italic',
      fontWeight: 'semibold',
    },
  ],
});

Font.register({
  family: MT_FONT_NAME,
  src: MTEverydaySansBold,
});

const StandardPDFColors = {
  black: '#000000',
  white: '#ffffff',
  gray: '#878787',
  descriptionTextGray: '#4F4F4F',
  tableBorder: '#C7C7C7',
};

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: StandardPDFColors.white,
    fontFamily: FONT_NAME,
    paddingBottom: 76,
  },
  main: {
    padding: '0px 28px',
    flexGrow: 1,
    fontFamily: FONT_NAME,
  },
  header: {
    height: '72px',
    backgroundColor: StandardPDFColors.black,
    padding: '10px 28px',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 28,
  },
  footer: {
    padding: '10px 0px 0px 28px',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  logo: {
    width: '133px',
  },
  spacer: {
    flexGrow: 1,
  },
  bold: {
    fontFamily: FONT_NAME,
    fontWeight: 600,
  },
  medium: {
    fontFamily: FONT_NAME,
    fontWeight: 500,
  },
  sizeBody: {
    fontSize: '12px',
  },
  sizeCaption: {
    fontSize: '10px',
  },
  sizeHeading: {
    fontSize: '16px',
  },
  sizeSubHeading: {
    fontSize: '14px',
  },
  sizeTitle: {
    fontSize: '20px',
  },
  footerTagline: {
    fontFamily: MT_FONT_NAME,
  },
});

const ConfideWhiteLogo = () => (
  <Svg width='134' height='36' viewBox='0 0 134 36'>
    <Path
      d='M35.709 18.0474C35.709 12.0035 39.6736 7.76172 45.3086 7.76172C50.396 7.76172 54.3051 11.2342 54.7695 16.1899H50.7218C50.3405 13.369 48.0949 11.3728 45.3086 11.3728C42.0233 11.3728 39.7291 14.1383 39.7291 18.0474C39.7291 21.9566 42.0302 24.7498 45.3086 24.7498C48.0949 24.7498 50.3405 22.7814 50.7218 19.9604H54.7695C54.3051 24.8884 50.4237 28.3609 45.3086 28.3609C39.6736 28.3609 35.709 24.0913 35.709 18.0474Z'
      fill='white'
    />
    <Path
      d='M55.7327 20.7823C55.7327 16.3534 58.713 13.2344 62.9271 13.2344C67.1412 13.2344 70.1216 16.3534 70.1216 20.7823C70.1216 25.2113 67.1412 28.3649 62.9271 28.3649C58.713 28.358 55.7327 25.239 55.7327 20.7823ZM62.9271 25.0519C65.0342 25.0519 66.5382 23.2706 66.5382 20.7823C66.5382 18.2941 65.0342 16.5405 62.9271 16.5405C60.8201 16.5405 59.316 18.2941 59.316 20.7823C59.316 23.2706 60.8201 25.0519 62.9271 25.0519Z'
      fill='white'
    />
    <Path
      d='M78.3213 16.401C76.3529 16.401 75.0914 17.9328 75.0914 20.3448V28.0868H71.3209V13.5038H75.0984V15.8257C75.9162 14.2385 77.7252 13.2266 79.7491 13.2266C83.0344 13.2266 85.0791 15.4445 85.0791 18.9447V28.0799H81.3017V19.7972C81.3017 17.7179 80.1511 16.401 78.3213 16.401Z'
      fill='white'
    />
    <Path
      d='M92.4672 7.48047H101.824V10.842H93.0148C92.384 10.842 92.0583 11.1193 92.0583 11.6391V13.4966H95.7456V16.9414H92.0583V28.0727H88.2808V16.9483H85.9797V13.5036H88.2808V11.6668C88.2808 8.92906 89.7294 7.48047 92.4672 7.48047ZM98.019 13.5036H101.79V28.0866H98.019V13.5036Z'
      fill='white'
    />
    <Path
      d='M103.107 20.7821C103.107 16.3531 105.706 13.2341 109.421 13.2341C111.202 13.2341 112.727 14.1144 113.746 15.6184V7.48828H117.517V28.0874H113.746V25.9804C112.734 27.4844 111.202 28.3647 109.421 28.3647C105.699 28.3647 103.107 25.2457 103.107 20.7821ZM110.184 25.0724C112.263 25.0724 113.739 23.2911 113.739 20.7751C113.739 18.2592 112.263 16.5333 110.184 16.5333C108.104 16.5333 106.683 18.2869 106.683 20.7751C106.683 23.2703 108.139 25.0724 110.184 25.0724Z'
      fill='white'
    />
    <Path
      d='M118.854 20.6983C118.854 16.3178 121.779 13.2266 125.965 13.2266C130.152 13.2266 133.077 16.5396 133.077 21.1904V21.9874H122.438C122.438 23.8242 123.942 25.1619 126.076 25.1619C127.774 25.1619 128.918 24.4202 129.382 23.1103H132.994C132.529 26.3124 129.764 28.364 125.965 28.364C121.779 28.364 118.854 25.2173 118.854 20.7052V20.6983ZM129.389 19.5546C129.389 17.7526 127.969 16.4634 125.972 16.4634C123.976 16.4634 122.444 17.7456 122.444 19.5546H129.389Z'
      fill='white'
    />
    <Path
      d='M15.7751 7.93608H21.1259C21.5834 7.93608 21.9576 7.5618 21.9576 7.10435V0.831729C21.9576 0.374278 21.5834 0 21.1259 0H14.8533C14.3958 0 14.0216 0.374278 14.0216 0.831729V6.18252C14.0216 6.63997 13.6473 7.01425 13.1898 7.01425H7.84598C7.3816 7.01425 7.00732 7.38852 7.00732 7.85291V13.1968C7.00732 13.6542 6.63304 14.0285 6.17559 14.0285H0.831729C0.374278 14.0285 0 14.4028 0 14.8602V18.6446C0 20.4813 1.48325 21.9646 3.31305 21.9646H4.11013C5.71121 21.9646 7.01425 23.2607 7.01425 24.8687V25.6034C7.01425 27.4401 8.4975 28.9234 10.3273 28.9234H11.1244C12.7255 28.9234 14.0285 30.2195 14.0285 31.8275V32.68C14.0285 34.5167 15.5117 36 17.3416 36H18.6377C20.4675 36 21.9507 34.5167 21.9507 32.68V31.3839C21.9507 29.5472 20.4675 28.0639 18.6377 28.0639H17.9861C16.3019 28.0639 14.9434 26.6985 14.9434 25.0212C14.9434 23.3439 16.3088 21.9784 17.9861 21.9784H18.6377C20.4675 21.9784 21.9507 20.4952 21.9507 18.6585V14.8672C21.9507 14.4097 21.5764 14.0354 21.119 14.0354H15.7682C15.3107 14.0354 14.9365 13.6611 14.9365 13.2037V8.77474C14.9365 8.30343 15.3107 7.93608 15.7751 7.93608ZM11.0065 20.9804H10.9511C9.28764 20.9804 7.93608 19.6288 7.93608 17.9653V15.7751C7.93608 15.3177 8.31036 14.9434 8.76781 14.9434H13.1968C13.6542 14.9434 14.0285 15.3177 14.0285 15.7751V17.9653C14.0216 19.6357 12.6769 20.9804 11.0065 20.9804Z'
      fill='white'
    />
    <Path
      d='M25.0283 22H24.9728C23.3094 22 21.9578 23.3516 21.9578 25.015C21.9578 26.6785 23.3094 28.03 24.9728 28.03H25.0283C26.6917 28.03 28.0433 26.6785 28.0433 25.015C28.0433 23.3516 26.6917 22 25.0283 22Z'
      fill='white'
    />
    <Path
      d='M31.1203 29.0312H31.0648C29.4014 29.0312 28.0498 30.3828 28.0498 32.0463C28.0498 33.7097 29.4014 35.0613 31.0648 35.0613H31.1203C32.7837 35.0613 34.1353 33.7097 34.1353 32.0463C34.1353 30.3828 32.7837 29.0312 31.1203 29.0312Z'
      fill='white'
    />
    <Path
      d='M27.2049 7.95703H22.7898C22.3254 7.95703 21.9512 8.33131 21.9512 8.79569V13.1553C21.9512 13.6197 22.3254 13.994 22.7898 13.994H27.2049C27.6693 13.994 28.0436 13.6197 28.0436 13.1553V8.79569C28.0436 8.33131 27.6693 7.95703 27.2049 7.95703Z'
      fill='white'
    />
    <Path
      d='M33.2969 1.11719H28.8818C28.4174 1.11719 28.0432 1.49146 28.0432 1.95585V6.31549C28.0432 6.77987 28.4174 7.15415 28.8818 7.15415H33.2969C33.7613 7.15415 34.1356 6.77987 34.1356 6.31549V1.95585C34.1356 1.4984 33.7613 1.11719 33.2969 1.11719Z'
      fill='white'
    />
  </Svg>
);

const Header = () => (
  <View style={styles.header} fixed>
    <ConfideWhiteLogo />
  </View>
);

const GrayLine = () => (
  <Svg width='539' height='10'>
    <Line
      x1='0'
      y1='0'
      x2='539'
      y2='0'
      strokeWidth={1}
      stroke={StandardPDFColors.gray}
    />
  </Svg>
);

const BrokenGrayLine = () => (
  <Svg width='539' height='1' viewBox='0 0 539 1'>
    <Line
      x1='4.37114e-08'
      y1='1'
      x2='539'
      y2='1'
      stroke={StandardPDFColors.gray}
      strokeDasharray='4'
    />
  </Svg>
);

const Footer = () => (
  <View style={{ position: 'absolute', bottom: 28 }} fixed>
    <View style={{ paddingLeft: '28px' }}>
      <GrayLine />
    </View>
    <View style={{ ...styles.footer, ...styles.sizeBody }}>
      <Text
        style={styles.sizeCaption}
        render={({ pageNumber }) => `Page ${pageNumber}`}
      />
    </View>
  </View>
);

export { styles, Footer, Header, GrayLine, BrokenGrayLine, StandardPDFColors };
